import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BellRing, BellOff, Loader2, Send } from "lucide-react";

const b64ToU8 = (v) => { const p = "=".repeat((4 - (v.length % 4)) % 4); const b = (v + p).replace(/-/g, "+").replace(/_/g, "/"); return Uint8Array.from(atob(b), (c) => c.charCodeAt(0)); };
const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const deviceLabel = () => /iphone|ipad/i.test(navigator.userAgent) ? "iPhone / iPad" : /android/i.test(navigator.userAgent) ? "Android" : "Ordinateur";

export function usePushSubscription() {
  const [state, setState] = useState({ supported: supported(), subscribed: false, devices: 0, loading: true, denied: false });
  const refresh = async () => {
    if (!supported()) { setState(s => ({ ...s, loading: false })); return; }
    try {
      const reg = await navigator.serviceWorker.ready; const sub = await reg.pushManager.getSubscription();
      const { data } = await api.get("/push/status");
      setState({ supported: true, subscribed: !!sub && data.subscriptions.some(s => s.endpoint === sub.endpoint), devices: data.devices, loading: false, denied: Notification.permission === "denied" });
    } catch { setState(s => ({ ...s, loading: false })); }
  };
  useEffect(() => { refresh(); }, []);
  const enable = async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Autorisation refusée par le navigateur"); await refresh(); return false; }
      const reg = await navigator.serviceWorker.ready; const { data } = await api.get("/push/public-key");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.publicKey) });
      await api.post("/push/subscribe", { ...sub.toJSON(), device: deviceLabel() });
      toast.success("Notifications push activées sur cet appareil"); await refresh(); return true;
    } catch (e) { toast.error("Activation impossible : " + (e?.message || "erreur")); await refresh(); return false; }
  };
  const disable = async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const reg = await navigator.serviceWorker.ready; const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.delete("/push/subscribe", { data: sub.toJSON() }); await sub.unsubscribe(); }
      toast.success("Notifications push désactivées");
    } catch { toast.error("Désactivation impossible"); }
    await refresh();
  };
  const test = async () => { try { await api.post("/push/test"); toast.success("Notification test envoyée"); } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); } };
  return { ...state, enable, disable, test };
}

export function PushSettingsCard() {
  const p = usePushSubscription();
  if (!p.supported) return (
    <div data-testid="push-unsupported" className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500 flex items-center gap-3"><BellOff className="w-5 h-5"/> Les notifications push ne sont pas disponibles dans ce navigateur. Sur iPhone, installez d'abord Orbit sur l'écran d'accueil (Partager → Ajouter à l'écran d'accueil).</div>
  );
  return (
    <div data-testid="push-settings-card" className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-4">
      <div className={`w-10 h-10 rounded-xl grid place-items-center ${p.subscribed ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}><BellRing className="w-5 h-5"/></div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-semibold text-slate-900">Notifications push sur ce téléphone</div>
        <div data-testid="push-status-text" className="text-xs text-slate-500 mt-0.5">
          {p.denied ? "Bloquées par le navigateur : autorisez les notifications dans les réglages du site." : p.subscribed ? `Activées · ${p.devices} appareil${p.devices > 1 ? "s" : ""} abonné${p.devices > 1 ? "s" : ""}. Vous recevrez les alertes automatiques (KPI, terrain, métier) même app fermée.` : `Recevez les alertes automatiques même lorsque l'app est fermée.${p.devices ? ` ${p.devices} autre(s) appareil(s) déjà abonné(s).` : ""}`}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {p.subscribed && <button onClick={p.test} data-testid="push-test-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold hover:bg-slate-50"><Send className="w-3.5 h-3.5"/> Tester</button>}
        <button onClick={p.subscribed ? p.disable : p.enable} disabled={p.loading || p.denied} data-testid="push-toggle-btn"
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold disabled:opacity-50 ${p.subscribed ? "border border-rose-200 text-rose-600 hover:bg-rose-50" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
          {p.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : p.subscribed ? <BellOff className="w-3.5 h-3.5"/> : <BellRing className="w-3.5 h-3.5"/>}
          {p.subscribed ? "Désactiver" : "Activer les notifications"}
        </button>
      </div>
    </div>
  );
}

export function PushPromptBanner() {
  const p = usePushSubscription();
  const [hidden, setHidden] = useState(() => localStorage.getItem("orbit_push_prompt_dismissed") === "1");
  if (hidden || !p.supported || p.loading || p.subscribed || p.denied) return null;
  return (
    <div data-testid="push-prompt-banner" className="mb-4 rounded-2xl bg-slate-900 text-white p-3.5 flex items-center gap-3">
      <BellRing className="w-5 h-5 text-amber-300 shrink-0"/>
      <div className="flex-1 text-sm"><span className="font-semibold">Recevez les alertes Orbit sur ce téléphone</span><span className="text-white/70"> — même quand l'app est fermée.</span></div>
      <button onClick={p.enable} data-testid="push-prompt-enable" className="px-3 py-1.5 rounded-lg bg-white text-slate-900 text-xs font-semibold">Activer</button>
      <button onClick={() => { localStorage.setItem("orbit_push_prompt_dismissed", "1"); setHidden(true); }} data-testid="push-prompt-dismiss" className="text-white/60 hover:text-white text-xs px-2">Plus tard</button>
    </div>
  );
}
